import React, { Fragment, useState } from "react";
import {
    Button,
    Card,
    CardBody,
    Col,
    Container,
    Form,
    FormGroup,
    Input,
    Label,
    Row,
} from "reactstrap";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import DataTable from "react-data-table-component";

import Breadcrumb from "../../../../layout/breadcrumb";
import orderReducer from "../../store";
import withReducer from "../../../../store/withReducer";
import ButtonLoader from "../../../../share-components/ui/buttonLoader";
import PreLoader from "../../../../share-components/ui/preLoader";

import {
    storeAlterationRequest,
} from "../../store/orderSlice";
import { showReturnListApi } from "../../../../services/order";

const CreateAlterationRequest = () => {
    const dispatch = useDispatch();
    const history = useHistory();

    const [orderIdInput, setOrderIdInput] = useState("");
    const [orderData, setOrderData] = useState(null);
    const [items, setItems] = useState([]);
    const [loadingOrder, setLoadingOrder] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [orderError, setOrderError] = useState("");
    const [serverErrors, setServerErrors] = useState({});

    const dataMenu = [
        {
            type: 1,
            text: "Alteration Requests",
            link: "/order/alteration-requests",
        },
        {
            type: 0,
            text: "Create",
        },
    ];

    const loadOrder = () => {
        setOrderError("");
        setServerErrors({});
        const raw = String(orderIdInput || "").trim();
        const idValue = raw.replace(/^#\s*/, "").trim();

        if (!idValue) {
            setOrderError("Please enter an order number");
            return;
        }
        if (!/^\d+$/.test(idValue)) {
            setOrderError("Order number must be numeric (e.g. 524107 or #524107)");
            return;
        }

        setLoadingOrder(true);
        setOrderData(null);
        setItems([]);
        showReturnListApi(idValue, { skipNotFoundRedirect: true })
            .then(({ response }) => {
                setLoadingOrder(false);
                const payload = response || {};
                const order = payload.data;
                const list = payload.list || [];

                if (!order) {
                    setOrderError("Order not found");
                    return;
                }

                if (order.fulfillment_status !== "fulfilled") {
                    setOrderError(
                        "Order must be fulfilled to create an alteration request"
                    );
                    return;
                }

                if (
                    !["paid", "partially_refunded"].includes(order.financial_status)
                ) {
                    setOrderError(
                        "Order must be paid or partially refunded to create an alteration request"
                    );
                    return;
                }

                const eligible = list
                    .filter(
                        (i) =>
                            i.is_goddiva === true &&
                            !i.return_request_id &&
                            !i.made_to_measure_request_id &&
                            !i.cancel_request_id &&
                            !i.refund_request_id &&
                            i.available_quantity > 0
                    )
                    .map((i) => ({
                        ...i,
                        selected: false,
                        length: "",
                        request_quantity: 1,
                        note: "",
                    }));

                setOrderData(order);
                setItems(eligible);

                if (eligible.length === 0) {
                    setOrderError(
                        "No eligible items found on this order for alteration"
                    );
                }
            })
            .catch((err) => {
                setLoadingOrder(false);
                const status = err?.response?.status;
                if (status === 404) {
                    setOrderError("Order not found");
                } else if (status === 403) {
                    setOrderError("You do not have permission to view this order");
                } else {
                    setOrderError("Failed to load order");
                }
            });
    };

    const updateItem = (rowId, field, value) => {
        setItems((prev) =>
            prev.map((row) => (row.id === rowId ? { ...row, [field]: value } : row))
        );
    };

    const fieldError = (index, field) => {
        const key = `items.${index}.${field}`;
        return serverErrors[key] ? serverErrors[key][0] : "";
    };

    const columns = [
        {
            name: "",
            cell: (row) => (
                <div className="only-checkbox checkbox checkbox-primary">
                    <input
                        type="checkbox"
                        checked={row.selected}
                        onChange={(e) => updateItem(row.id, "selected", e.target.checked)}
                        id={`select-${row.id}`}
                    />
                    <label htmlFor={`select-${row.id}`}></label>
                </div>
            ),
            width: "60px",
            ignoreRowClick: true,
            allowOverflow: true,
            button: true,
        },
        {
            name: "Product",
            cell: (row) => (
                <div className="d-flex align-items-center py-2">
                    <img
                        src={row.image}
                        alt=""
                        className="mr-2"
                        style={{ width: 50 }}
                    />
                    <div>
                        <p className="m-0 text-dark font-weight-700">{row.name}</p>
                        <p className="m-0 text-muted">{row.sku}</p>
                        <p className="m-0 text-muted">
                            {row.colour}/{row.size}
                        </p>
                    </div>
                </div>
            ),
            grow: 2,
        },
        {
            name: "Available Qty",
            selector: (row) => row.available_quantity,
            center: true,
            grow: 0.5,
        },
        {
            name: "Hem Length (cm)",
            cell: (row, index) => {
                const err = fieldError(index, "length");
                return (
                    <div style={{ width: "100%" }}>
                        <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={row.length}
                            disabled={!row.selected}
                            onChange={(e) =>
                                updateItem(row.id, "length", e.target.value)
                            }
                            className={"form-control form-control-sm" + (err ? " is-invalid" : "")}
                            style={{ width: 90 }}
                        />
                        {err && <small className="text-danger">{err}</small>}
                    </div>
                );
            },
            grow: 0.7,
        },
        {
            name: "Quantity",
            cell: (row, index) => {
                const err = fieldError(index, "quantity");
                return (
                    <div style={{ width: "100%" }}>
                        <input
                            type="number"
                            min="1"
                            max={row.available_quantity}
                            value={row.request_quantity}
                            disabled={!row.selected}
                            onChange={(e) =>
                                updateItem(row.id, "request_quantity", e.target.value)
                            }
                            className={"form-control form-control-sm" + (err ? " is-invalid" : "")}
                            style={{ width: 80 }}
                        />
                        {err && <small className="text-danger">{err}</small>}
                    </div>
                );
            },
            grow: 0.7,
        },
        {
            name: "Note",
            cell: (row) => (
                <textarea
                    rows={2}
                    maxLength={1000}
                    value={row.note}
                    disabled={!row.selected}
                    onChange={(e) => updateItem(row.id, "note", e.target.value)}
                    className="form-control form-control-sm"
                    style={{ minHeight: 60, resize: "vertical" }}
                />
            ),
            grow: 1.5,
        },
    ];

    const onSubmit = () => {
        setServerErrors({});

        const selected = items.filter((i) => i.selected);
        if (selected.length === 0) {
            toast.error("Please select at least one item", {
                position: toast.POSITION.TOP_RIGHT,
            });
            return;
        }

        const invalid = selected.find(
            (i) =>
                !i.length ||
                isNaN(parseFloat(i.length)) ||
                parseFloat(i.length) <= 0 ||
                !i.request_quantity ||
                parseInt(i.request_quantity, 10) <= 0 ||
                parseInt(i.request_quantity, 10) > parseInt(i.available_quantity, 10)
        );
        if (invalid) {
            toast.error("Please enter a valid length and quantity for each selected item", {
                position: toast.POSITION.TOP_RIGHT,
            });
            return;
        }

        const payload = {
            order: orderData.id,
            items: selected.map((i) => ({
                id: i.id,
                length: parseFloat(i.length),
                quantity: parseInt(i.request_quantity, 10),
                note: i.note || "",
            })),
        };

        setSubmitting(true);
        dispatch(storeAlterationRequest(payload))
            .then((res) => {
                setSubmitting(false);
                if (res.error) {
                    const data = res.payload?.data;
                    if (res.payload?.status === 422 && data?.errors) {
                        setServerErrors(data.errors);
                        toast.error("Please correct the highlighted errors", {
                            position: toast.POSITION.TOP_RIGHT,
                        });
                    }
                    return;
                }
                history.push("/order/alteration-requests", { from: "submit" });
            })
            .catch(() => setSubmitting(false));
    };

    return (
        <Fragment>
            <Breadcrumb title="Create Alteration Request" data={dataMenu} />
            <Container fluid={true}>
                <Card>
                    <CardBody>
                        <Row className="align-items-end">
                            <Col md={4}>
                                <FormGroup>
                                    <Label className="font-weight-bold">Order Number</Label>
                                    <Input
                                        type="text"
                                        placeholder="Enter Order Number (e.g. 524107)"
                                        value={orderIdInput}
                                        onChange={(e) => setOrderIdInput(e.target.value)}
                                        onKeyDown={(e) => {
                                            if (e.key === "Enter") {
                                                e.preventDefault();
                                                loadOrder();
                                            }
                                        }}
                                    />
                                    {orderError && (
                                        <small className="text-danger">{orderError}</small>
                                    )}
                                    {serverErrors.order && (
                                        <small className="text-danger d-block">
                                            {serverErrors.order[0]}
                                        </small>
                                    )}
                                </FormGroup>
                            </Col>
                            <Col md={2}>
                                <FormGroup>
                                    <Button
                                        color="primary"
                                        onClick={loadOrder}
                                        disabled={loadingOrder}
                                    >
                                        {loadingOrder ? "Loading..." : "Load Order"}
                                    </Button>
                                </FormGroup>
                            </Col>
                        </Row>

                        {loadingOrder && <PreLoader />}

                        {orderData && items.length > 0 && (
                            <>
                                <div className="mt-3 mb-2">
                                    <strong>Order:</strong> {orderData.order_name}
                                    {orderData.customer_name && (
                                        <span className="ml-3">
                                            <strong>Customer:</strong> {orderData.customer_name}
                                        </span>
                                    )}
                                </div>

                                <DataTable
                                    columns={columns}
                                    data={items}
                                    highlightOnHover
                                />

                                <div className="d-flex justify-content-end mt-3">
                                    <ButtonLoader
                                        disabled={submitting}
                                        onClick={onSubmit}
                                        btntext="Create Alteration Request"
                                        className="btn btn-primary"
                                    />
                                </div>
                            </>
                        )}
                    </CardBody>
                </Card>
            </Container>
        </Fragment>
    );
};

export default withReducer([{ order: orderReducer }])(CreateAlterationRequest);
